import React, { FC, useCallback, useMemo } from "react";
import { Box, Icon, View } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Text } from "@lookiero/sty-psp-ui";
import { useGetExperiments } from "../../../../behaviors/useGetExperiments";
import { EmptyStateBanner } from "../../../../components/emptyStateBanner/EmptyStateBanner";
import { useStaticInfo } from "../../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { useTrackEmptyState } from "../../../../tracking/useTrackEmptyState";
import { style as unlockYourCompleteYourLookBannerStyle } from "./UnlockYourCompleteYourLookBanner.style";

interface UnlockYourCompleteYourLookBannerProps {
  readonly onBack: () => void;
}

const UnlockYourCompleteYourLookBanner: FC<UnlockYourCompleteYourLookBannerProps> = ({ onBack }) => {
  const {
    cdnPath,
    session: {
      user: { country, segment, id: userId },
    },
    tradename,
  } = useStaticInfo();
  const { experiments } = useGetExperiments();

  const cylEmptyStateUnlockTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CYL_EMPTY_STATE_UNLOCK_TITLE,
  });
  const cylEmptyStateUnlockBenefit1Text = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CYL_EMPTY_STATE_UNLOCK_BENEFIT1,
  });
  const cylEmptyStateUnlockBenefit2Text = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CYL_EMPTY_STATE_UNLOCK_BENEFIT2,
  });
  const cylEmptyStateUnlockBenefit3Text = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CYL_EMPTY_STATE_UNLOCK_BENEFIT3,
  });

  const style = useMemo(() => unlockYourCompleteYourLookBannerStyle(), []);

  const trackEmptyState = useTrackEmptyState({
    country,
    segment,
    userId,
    tradename,
  });

  const imageUrl = useMemo(() => `${cdnPath}/images/shop/empty-states/unlock/${segment}.webp`, [cdnPath, segment]);

  const onLayout = useCallback(() => {
    trackEmptyState({
      type: "UnlockYourCYL",
      experiments,
    });
  }, [trackEmptyState, experiments]);

  return (
    <EmptyStateBanner
      cta={I18nMessages.CYL_EMPTY_STATE_UNLOCK_CTA}
      imageUrl={imageUrl}
      subtitle={I18nMessages.CYL_EMPTY_STATE_UNLOCK_SUBTITLE}
      title={cylEmptyStateUnlockTitleText}
      onLayout={onLayout}
      onPress={onBack}
    >
      <View style={style.bulletsList}>
        <Box style={style.bulletItem}>
          <Box style={style.bulletItemIconWrapper}>
            <Icon name="calendar" />
          </Box>

          <Text level={2} style={style.bulletItemText} variant="detail">
            {cylEmptyStateUnlockBenefit1Text}
          </Text>
        </Box>
        <Box style={style.bulletItem}>
          <Box style={style.bulletItemIconWrapper}>
            <Icon name="hanger" />
          </Box>

          <Text level={2} style={style.bulletItemText} variant="detail">
            {cylEmptyStateUnlockBenefit2Text}
          </Text>
        </Box>
        <Box style={style.bulletItem}>
          <Box style={style.bulletItemIconWrapper}>
            <Icon name="basket" />
          </Box>

          <Text level={2} style={style.bulletItemText} variant="detail">
            {cylEmptyStateUnlockBenefit3Text}
          </Text>
        </Box>
      </View>
    </EmptyStateBanner>
  );
};

export { UnlockYourCompleteYourLookBanner };
